import React from "react";

import classNames from "classnames";
import isEmpty from "lodash.isempty";
import omitBy from "lodash.omitby";
import { observer } from "mobx-react";
import PropTypes from "prop-types";
import styled from "styled-components";

import ComponentContext from "../../contexts/ComponentContext";
import { useStores } from "../../contexts/StoreContext";
import useDevice from "../../shared/customHooks/useDevice";
// eslint-disable-next-line import/no-cycle
import componentMap from "./componentMap";

const StyledBackground = styled.div`
  height: 100%;
  background-color: ${({ $backgroundColor }) =>
    $backgroundColor || "transparent"};
`;

const StyledComponentWrapper = styled.div`
  & > div:only-child {
    position: relative;
    height: 100%;
  }

  height: fit-content;
  background-image: ${({ componentBackground }) =>
    componentBackground ? `url(${componentBackground})` : null};
  background-repeat: no-repeat;
  background-position: ${({ $backgroundPosition }) =>
    $backgroundPosition ? `${$backgroundPosition}` : "center"};
`;

const GridLayout = styled.main`
  display: grid !important;
  grid-template-columns: repeat(12, 1fr);
  grid-auto-rows: minmax(min-content, max-content);
`;

// Component for grid item
const GridItem = styled.div`
  height: 100%;
  grid-area: ${(props) => props.$gridArea};
`;

function CustomPage(props) {
  const { pageAttributes, slug, contentKey = "content" } = props;
  const { isMobile } = useDevice();

  const { configStore } = useStores();
  const { config } = configStore;
  const { componentTemplates = [] } = config;

  const content = pageAttributes[contentKey];

  const isDeepEmpty = (key) => {
    if (typeof key === "object") {
      if (isEmpty(key?.data)) return true;
      if (isEmpty(key)) return true;
    }
    return isEmpty(key);
  };

  const mergeWithTemplate = (template, component) => ({
    ...template,
    ...omitBy(component, isDeepEmpty),
  });

  if (isEmpty(pageAttributes?.content)) {
    return null;
  }

  return (
    <GridLayout>
      {content.map((strapiComponent, index) => {
        const {
          __component: componentName,
          id: componentId,
          useTemplate,
        } = strapiComponent;

        if (!componentMap[componentName]) {
          return null;
        }

        const template = componentTemplates.find(
          ({ __component: name }) => name === componentName
        );

        const isTemplateUsed = useTemplate && !isEmpty(template);

        const componentData = isTemplateUsed
          ? mergeWithTemplate(template, strapiComponent)
          : strapiComponent;

        const {
          backgroundColor = null,
          componentClassName = null,
          wrapperClassName = null,
          componentBackground = null,
          backgroundPosition = "null",
        } = componentData;

        const { additionalProps, Component } = componentMap[componentName];

        const fullWidthComponent =
          componentData.fullWidth || additionalProps?.fullWidth;

        const backgroundClassNames = classNames({
          [wrapperClassName]: !isEmpty(wrapperClassName),
        });

        const componentClassNames = classNames({
          [componentClassName]: !isEmpty(componentClassName),
          "container-xxl": !fullWidthComponent,
          "use-template": isTemplateUsed,
        });

        const safeComponentName = componentName.replace(".", "-");

        const componentKey = `${safeComponentName}-${componentId}`;

        const componentBackgroundUrl = componentBackground?.url;

        const gridMapping =
          (isMobile
            ? pageAttributes.mobileLayout
            : pageAttributes.desktopLayout
          )
            ?.split(";")
            .map((str) => str.trim()) || [];

        const gridArea = gridMapping[index] || `auto / 1 / auto / 13`; // Fallback to 'auto' if the index exceeds predefined grid areas

        return (
          <GridItem key={componentKey} $gridArea={gridArea}>
            <ComponentContext.Provider value={componentData}>
              <StyledBackground
                $backgroundColor={backgroundColor}
                className={backgroundClassNames}
                id={componentData?.uniqueId || componentKey}
              >
                <StyledComponentWrapper
                  $backgroundPosition={!isMobile ? backgroundPosition : null}
                  className={componentClassNames}
                  componentBackground={
                    !isMobile ? componentBackgroundUrl : null
                  }
                  id={`${slug}-${componentKey}-content`}
                >
                  <Component {...additionalProps} />
                </StyledComponentWrapper>
              </StyledBackground>
            </ComponentContext.Provider>
          </GridItem>
        );
      })}
    </GridLayout>
  );
}

CustomPage.propTypes = {
  pageAttributes: PropTypes.shape({
    content: PropTypes.arrayOf(
      PropTypes.shape({
        __component: PropTypes.string,
        componentClassName: PropTypes.string,
        fullWidth: PropTypes.bool,
        id: PropTypes.number,
        wrapperClassName: PropTypes.string,
      })
    ),
  }).isRequired,
  slug: PropTypes.string,
};

CustomPage.defaultProps = {
  slug: "",
};

export default observer(CustomPage);
